import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import requestsAPI from '../../utils/api/requests';

export const fetchRequests = createAsyncThunk(
  'requests/fetchRequests',
  async ({ startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.getAll({ startDate, endDate });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateRequestStatus = createAsyncThunk(
  'requests/updateRequestStatus',
  async ({ rqstSn, status }, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.updateStatus(rqstSn, status);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createRequest = createAsyncThunk(
  'requests/createRequest',
  async ({ payload, signatureFile }, { rejectWithValue }) => {
    try {
      if (!signatureFile) {
        return rejectWithValue({ message: 'Signature file is required' });
      }

      const response = await requestsAPI.create(payload);
      const rqstSn = response?.data?.rqst_sn;
      if (!rqstSn) {
        return rejectWithValue({ message: 'Request created but signature upload failed' });
      }

      const formData = new FormData();
      formData.append('files', signatureFile);
      try {
        await requestsAPI.uploadSignature(rqstSn, formData);
      } catch (uploadError) {
        try {
          await requestsAPI.updateStatus(rqstSn, 'CANCELLED');
        } catch (_cancelError) {
          // Best-effort rollback so an unsigned request is not left OPEN.
        }
        return rejectWithValue(uploadError.response?.data || uploadError.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadFiles = createAsyncThunk(
  'requests/uploadFiles',
  async ({ rqstSn, formData }, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.upload(rqstSn, formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRequestFiles = createAsyncThunk(
  'requests/fetchRequestFiles',
  async ({ rqstSn }, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.getFiles(rqstSn);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFileDownloadUrl = createAsyncThunk(
  'requests/fetchFileDownloadUrl',
  async ({ fileUuid, inline }, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.getFileDownloadUrl(fileUuid, { inline });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteRequestFile = createAsyncThunk(
  'requests/deleteRequestFile',
  async ({ fileUuid }, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.deleteFile(fileUuid);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRequestWarnings = createAsyncThunk(
  'requests/fetchRequestWarnings',
  async ({ personId, officeUserUuid }, { rejectWithValue }) => {
    try {
      const response = await requestsAPI.getWarnings({ personId, officeUserUuid });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  requestsList: [],
  files: [],
  downloadUrl: null,
  downloadUrlLoading: false,
  loading: false,
  filesLoading: false,
  updating: false,
  creating: false,
  uploading: false,
  warnings: null,
  warningsLoading: false,
  error: null,
};

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    clearRequestsError: (state) => {
      state.error = null;
    },
    clearRequestsState: (state) => {
      state.requestsList = [];
      state.files = [];
      state.downloadUrl = null;
      state.loading = false;
      state.filesLoading = false;
      state.downloadUrlLoading = false;
      state.updating = false;
      state.creating = false;
      state.uploading = false;
      state.error = null;
    },
    clearFiles: (state) => {
      state.files = [];
    },
    clearDownloadUrl: (state) => {
      state.downloadUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requestsList = action.payload?.data || [];
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.requestsList = [];
      })
      .addCase(updateRequestStatus.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateRequestStatus.fulfilled, (state, action) => {
        state.updating = false;
        const updated = action.payload?.data;
        if (!updated?.rqst_sn) return;
        const idx = state.requestsList.findIndex((r) => r.rqst_sn === updated.rqst_sn);
        if (idx !== -1) state.requestsList[idx] = updated;
      })
      .addCase(updateRequestStatus.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      })
      .addCase(createRequest.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createRequest.fulfilled, (state) => {
        state.creating = false;
        // We refresh after create to get included `user` data; avoid optimistic insert here.
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      })
      .addCase(uploadFiles.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadFiles.fulfilled, (state, action) => {
        state.uploading = false;
        state.files = action.payload?.data || [];
      })
      .addCase(uploadFiles.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })
      .addCase(fetchRequestFiles.pending, (state) => {
        state.filesLoading = true;
        state.error = null;
      })
      .addCase(fetchRequestFiles.fulfilled, (state, action) => {
        state.filesLoading = false;
        state.files = action.payload?.data || [];
      })
      .addCase(fetchRequestFiles.rejected, (state, action) => {
        state.filesLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFileDownloadUrl.pending, (state) => {
        state.downloadUrlLoading = true;
        state.error = null;
      })
      .addCase(fetchFileDownloadUrl.fulfilled, (state, action) => {
        state.downloadUrlLoading = false;
        state.downloadUrl = action.payload?.data || null;
      })
      .addCase(fetchFileDownloadUrl.rejected, (state, action) => {
        state.downloadUrlLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteRequestFile.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteRequestFile.fulfilled, (state, action) => {
        const deletedUuid = action.payload?.data?.uuid;
        if (deletedUuid) {
          state.files = state.files.filter((file) => file.uuid !== deletedUuid);
        }
      })
      .addCase(deleteRequestFile.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchRequestWarnings.pending, (state) => {
        state.warningsLoading = true;
        state.warnings = null;
      })
      .addCase(fetchRequestWarnings.fulfilled, (state, action) => {
        state.warningsLoading = false;
        state.warnings = action.payload?.data || null;
      })
      .addCase(fetchRequestWarnings.rejected, (state) => {
        state.warningsLoading = false;
        state.warnings = null;
      });
  },
});

export const { clearRequestsError, clearRequestsState, clearFiles, clearDownloadUrl } = requestsSlice.actions;
export default requestsSlice.reducer;

